import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, ReferenceArea,
} from 'recharts';
import { MdInsights, MdDownload, MdWarning, MdPictureAsPdf, MdChevronRight } from 'react-icons/md';
import toast from 'react-hot-toast';
import { useYearlyFinance } from '../../hooks/useYearlyFinance';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import useAuth from '../../hooks/useAuth';
import { adToBs, bsToAd, bsSpanLabel } from '../../utils/calendar';
import { exportYearlyFinanceToPDF } from '../../utils/pdfExport';

const fmtNPR     = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtPercent = (n) => `${Number(n || 0).toFixed(1)}%`;
const shortLabel = (monthLabel) => String(monthLabel || '').split(' ')[0]; // "January 2026" → "January", "Asar 2083" → "Asar"

// YoY helper → { text, positive }. lowerIsBetter flips the color logic (e.g. expenses).
function yoyChange(cur, prior, lowerIsBetter = false) {
  if (!prior) {
    if (!cur) return { text: 'no change vs last year', positive: null };
    return { text: 'new vs last year', positive: !lowerIsBetter };
  }
  const pct  = ((cur - prior) / Math.abs(prior)) * 100;
  const grew = cur > prior;
  const sign = pct >= 0 ? '+' : '';
  return { text: `${sign}${Math.round(pct)}% vs last year`, positive: lowerIsBetter ? !grew : grew };
}

function YoYNote({ change }) {
  if (!change || change.positive === null) {
    return <p className="text-xs text-muted mt-1">{change?.text || ''}</p>;
  }
  return (
    <p className={`text-xs mt-1 ${change.positive ? 'text-green-600' : 'text-red-600'}`}>{change.text}</p>
  );
}

function StatCard({ label, value, tint, valueColor, change }) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${tint}`}>
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className={`text-xl font-bold mt-1 ${valueColor || 'text-secondary'}`}>{value}</p>
      <YoYNote change={change} />
    </div>
  );
}

function SkeletonCard() {
  return <div className="h-24 bg-nav-hover animate-pulse rounded-2xl" />;
}

export default function FinanceReports({ onDrillToMonth }) {
  const currentTheme = useTheme()?.currentTheme;
  const { club } = useTenant();
  const { user } = useAuth();
  // Finance grouping stays on the club's STORED (now-frozen) defaultCalendar — NOT the display
  // calendar, which is now unconditionally BS. This keeps every existing club's per-month/year
  // numbers byte-identical (Option A). Admins already saw exactly this value before the toggle
  // was retired. defaultCalendar is a frozen dead field, scheduled to move to BS separately.
  const financeCalendar = club?.defaultCalendar || 'ad';
  const isBs = financeCalendar === 'bs';
  const currentYear = isBs ? adToBs(new Date()).year : new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [metric, setMetric] = useState('net'); // 'net' | 'income'
  const [pdfLoading, setPdfLoading] = useState(false);

  // Reset year if the club calendar flips (BS↔AD years aren't interchangeable).
  useEffect(() => { setYear(currentYear); }, [financeCalendar]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading, isError, refetch } = useYearlyFinance(year, financeCalendar);

  const years = [0, 1, 2, 3, 4].map(i => currentYear - i);

  const handleExportCsv = () => {
    if (!data) return;
    const header = ['Month', 'Income (NPR)', 'Expenses (NPR)', 'Net (NPR)', 'Margin (%)'];
    const rows = data.months.map(m => [
      m.monthLabel, m.income, m.expenses, m.net, m.margin,
    ]);
    const totalRow = ['TOTAL', data.totals.income, data.totals.expenses, data.totals.net, data.totals.margin];
    const csv = [header, ...rows, totalRow]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trainaxis-finance-${year}-${financeCalendar}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const handleExportPdf = async () => {
    if (!data || pdfLoading) return;
    setPdfLoading(true);
    try {
      await exportYearlyFinanceToPDF(data, club, user, financeCalendar);
    } catch (e) {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  // Drill into a month → hand the parent an AD "YYYY-MM" (what FinanceOverview expects).
  // For BS we use the same day-1 → AD-month mapping DualDatePicker uses, so it round-trips.
  const handleDrill = (m) => {
    if (!onDrillToMonth) return;
    let monthStr;
    if (isBs) {
      const ad = bsToAd(year, m.monthIndex, 1);
      monthStr = `${ad.getFullYear()}-${String(ad.getMonth() + 1).padStart(2, '0')}`;
    } else {
      monthStr = `${year}-${String(m.monthIndex).padStart(2, '0')}`;
    }
    onDrillToMonth(monthStr);
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
        <div className="h-72 bg-nav-hover animate-pulse rounded-2xl" />
      </div>
    );
  }

  // ── Error ──
  if (isError || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <MdWarning className="mx-auto text-4xl text-amber-500 mb-3" />
          <p className="text-sm font-semibold text-secondary mb-2">Unable to load reports</p>
          <p className="text-xs text-muted mb-4">There was an error fetching the yearly breakdown.</p>
          <button onClick={() => refetch()}
            className={`px-4 py-2 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-xs font-medium hover:opacity-90 transition-all duration-200`}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { months, priorYearMonths, totals, priorYearTotals } = data;
  const isEmpty = totals.income === 0 && totals.expenses === 0;

  const incomeChange  = yoyChange(totals.income, priorYearTotals.income);
  const expenseChange = yoyChange(totals.expenses, priorYearTotals.expenses, true);
  const netChange     = yoyChange(totals.net, priorYearTotals.net);
  const marginDiff    = (totals.margin - priorYearTotals.margin);
  const marginChange  = {
    text: `${marginDiff >= 0 ? '+' : ''}${(Math.round(marginDiff * 10) / 10)} pts vs last year`,
    positive: marginDiff >= 0,
  };

  // Only draw the prior-year line if that year actually has recorded data — otherwise
  // a flat dashed line at zero looks like a broken control.
  const priorHasData = priorYearTotals.income > 0 || priorYearTotals.expenses > 0;

  const chartData = months.map((m, i) => ({
    label: shortLabel(m.monthLabel),
    thisYear: m[metric],
    lastYear: priorYearMonths[i]?.[metric] ?? 0,
  }));

  const netColor = (n) => (n > 0 ? 'text-green-600' : n < 0 ? 'text-red-600' : 'text-subtle');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-secondary">Reports</h2>
        <p className="text-sm text-muted mt-0.5">Yearly performance breakdown and trends.</p>
      </div>

      {/* Year selector + export */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="px-3 py-2 rounded-full border border-divider text-sm bg-card text-secondary outline-none focus:border-secondary">
          {years.map(y => <option key={y} value={y}>{y}{isBs ? ' BS' : ''}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCsv}
            className="flex items-center gap-1.5 px-3 py-2 bg-card text-secondary border border-divider rounded-full text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200">
            <MdDownload className="text-base" /> Export CSV
          </button>
          <button
            onClick={handleExportPdf}
            disabled={pdfLoading}
            className={`flex items-center gap-1.5 px-3 py-2 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-60`}>
            {pdfLoading
              ? <span className="w-4 h-4 border-2 border-card border-t-transparent rounded-full animate-spin" />
              : <MdPictureAsPdf className="text-base" />}
            Export PDF
          </button>
        </div>
      </div>

      {/* Clarify that finance groups by Gregorian months (unlike the BS display elsewhere), with
          the accurate BS span for reference. Only shown while grouping is AD. */}
      {!isBs && (
        <p className="text-xs text-muted -mt-2">
          Financial months follow the Gregorian calendar (January–December) · {year} ≈ {bsSpanLabel(new Date(year, 0, 1), new Date(year, 11, 31))}
        </p>
      )}

      {isEmpty ? (
        <div className="py-20 text-center">
          <MdInsights className="mx-auto text-5xl text-subtle mb-4" />
          <p className="text-base font-semibold text-secondary">No financial data for this year.</p>
          <p className="text-sm text-muted mt-2">Income and expenses recorded for {year}{isBs ? ' BS' : ''} will appear here.</p>
        </div>
      ) : (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total Income"   value={fmtNPR(totals.income)}   tint="bg-green-50 border-green-200" valueColor="text-green-700" change={incomeChange} />
            <StatCard label="Total Expenses" value={fmtNPR(totals.expenses)} tint="bg-red-50 border-red-200"     valueColor="text-red-700"   change={expenseChange} />
            <StatCard label="Net Profit"     value={fmtNPR(totals.net)}      tint="bg-nav-hover border-divider"   valueColor={netColor(totals.net)} change={netChange} />
            <StatCard label="Margin"         value={totals.income === 0 ? '—' : fmtPercent(totals.margin)} tint="bg-nav-hover border-divider" change={marginChange} />
          </div>

          {/* Monthly breakdown table */}
          <div className="bg-card rounded-2xl border border-divider shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-divider">
              <h3 className="text-sm font-bold text-secondary">Monthly Breakdown</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-divider">
                    {['Month', 'Income', 'Expenses', 'Net', 'Margin %'].map(h => (
                      <th key={h} className={`px-4 py-2.5 text-xs font-semibold text-muted uppercase tracking-wide ${h === 'Month' ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {months.map(m => {
                    const blank = m.income === 0 && m.expenses === 0;
                    return (
                      <tr
                        key={m.monthIndex}
                        onClick={() => handleDrill(m)}
                        className="border-b border-divider last:border-0 hover:bg-nav-hover transition-colors cursor-pointer group">
                        <td className="px-4 py-2.5 text-secondary whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            {m.monthLabel}
                            <MdChevronRight className="text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-muted">{blank ? '—' : fmtNPR(m.income)}</td>
                        <td className="px-4 py-2.5 text-right text-muted">{blank ? '—' : fmtNPR(m.expenses)}</td>
                        <td className={`px-4 py-2.5 text-right font-medium ${blank ? 'text-subtle' : netColor(m.net)}`}>{blank ? '—' : fmtNPR(m.net)}</td>
                        <td className="px-4 py-2.5 text-right text-muted">{blank || m.income === 0 ? '—' : fmtPercent(m.margin)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-divider bg-nav-hover/40 font-bold">
                    <td className="px-4 py-3 text-secondary">TOTAL</td>
                    <td className="px-4 py-3 text-right text-secondary">{fmtNPR(totals.income)}</td>
                    <td className="px-4 py-3 text-right text-secondary">{fmtNPR(totals.expenses)}</td>
                    <td className={`px-4 py-3 text-right ${netColor(totals.net)}`}>{fmtNPR(totals.net)}</td>
                    <td className="px-4 py-3 text-right text-secondary">{totals.income === 0 ? '—' : fmtPercent(totals.margin)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Year-over-Year chart */}
          <div className="bg-card rounded-2xl border border-divider p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div>
                <h3 className="text-sm font-bold text-secondary">Year-over-Year Comparison</h3>
                <p className="text-xs text-muted mt-1">
                  {year}{isBs ? ' BS' : ''}{priorHasData ? ` vs ${year - 1}${isBs ? ' BS' : ''}` : ''}
                </p>
              </div>
              <div className="flex items-center border border-divider rounded-lg overflow-hidden text-xs">
                {['net', 'income'].map(opt => (
                  <button key={opt} onClick={() => setMetric(opt)}
                    className={`px-3 py-1.5 capitalize transition-colors ${metric === opt ? 'bg-secondary text-card font-semibold' : 'text-muted hover:text-secondary'}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                {/* Subtle red tint over the loss region (below zero). Invisible when all values are positive. */}
                <ReferenceArea y1={0} y2={-1e9} fill="#E24B4A" fillOpacity={0.06} ifOverflow="hidden" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="var(--color-muted)" interval={0} angle={-30} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 12 }} stroke="var(--color-muted)" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                {/* Zero baseline — separates profit (above) from loss (below). */}
                <ReferenceLine y={0} stroke="#6b7280" strokeWidth={1.5} ifOverflow="extendDomain" />
                <Tooltip
                  formatter={(v) => fmtNPR(v)}
                  contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px' }}
                  labelStyle={{ color: 'var(--color-secondary)' }}
                />
                <Legend />
                <Line type="monotone" dataKey="thisYear" name={`${year}${isBs ? ' BS' : ''}`} stroke="#E24B4A" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                {priorHasData && (
                  <Line type="monotone" dataKey="lastYear" name={`${year - 1}${isBs ? ' BS' : ''}`} stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted mt-2">
              <span className="inline-block w-3 h-0.5 align-middle mr-1" style={{ background: '#E24B4A' }} /> {metric === 'net' ? 'Net' : 'Income'}, {year}{isBs ? ' BS' : ''}
              {priorHasData && (
                <>
                  <span className="inline-block w-3 h-0.5 align-middle mx-1 ml-3" style={{ background: '#9ca3af' }} /> {metric === 'net' ? 'Net' : 'Income'}, {year - 1}{isBs ? ' BS' : ''} (dashed)
                </>
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
